/**
 * Attribution forwarding for Meta Ads → Impultienda (Good Me Flow).
 *
 * - Captures utm_source, utm_medium, utm_campaign, utm_term, utm_content,
 *   utm_id and fbclid (plus any other utm_* key) from the landing URL.
 * - Keeps them for the whole browser session (sessionStorage), so they
 *   survive in-page navigation and anchor jumps.
 * - Appends them to the checkout URL on every purchase click, preserving the
 *   original link and its existing query parameters — a parameter already
 *   present on the checkout URL is never duplicated or overwritten.
 */
const STORAGE_KEY = 'wpp_utm_params'

export const TRACKED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'fbclid',
] as const

type ParamList = Array<[string, string]>

const isTracked = (key: string) => {
  const lower = key.toLowerCase()
  return (TRACKED_PARAMS as readonly string[]).includes(lower) || lower.startsWith('utm_')
}

function readFromUrl(): ParamList {
  if (typeof window === 'undefined') return []
  const found: ParamList = []
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (value && isTracked(key)) found.push([key, value])
  })
  return found
}

function readStored(): ParamList {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY)
    return stored ? (JSON.parse(stored) as ParamList) : []
  } catch {
    return []
  }
}

/**
 * Reads the tracking parameters for this session. A new ad click (URL with
 * tracking parameters) replaces the stored set; otherwise the stored set wins.
 */
export function captureTrackingParams(): ParamList {
  if (typeof window === 'undefined') return []
  const fromUrl = readFromUrl()
  if (fromUrl.length > 0) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl))
    } catch {
      // Storage can be unavailable (private mode, in-app browsers) — URL still wins.
    }
    return fromUrl
  }
  return readStored()
}

export function withUtmParams(checkoutUrl: string): string {
  const params = captureTrackingParams()
  if (params.length === 0) return checkoutUrl

  try {
    const url = new URL(checkoutUrl)
    params.forEach(([key, value]) => {
      if (!url.searchParams.has(key)) url.searchParams.set(key, value)
    })
    return url.toString()
  } catch {
    return checkoutUrl
  }
}
